import { useEffect, useRef } from "react";
import camelcaseKeys from "camelcase-keys";
import queryString from "query-string";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import BillingSuccessfulPaymentModal from "@/components/BillingSuccessfulPaymentModal";
import * as companySessionActions from "@/components/new/CompanySession/company-session.actions";
import { SOCKET_URL } from "@/env";
import * as accountingActions from "@/redux/accounting/accounting.actions";
import * as authenticationActions from "@/redux/authentication/authentication.actions";
import customers from "@/redux/modules/customers";
import * as notificationsActions from "@/redux/notifications/notifications.actions";
import * as plannerActions from "@/redux/planner/planner.actions";
import * as plannerSelectors from "@/redux/planner/planner.selectors";
import * as billingActions from "@/redux/settings/billing/billing.actions";
import store from "@/redux/store";
import * as timezonesActions from "@/redux/timezones/timezones.actions";
import { identifyClarityUser } from "@/utils/clarity";
import { isDefinitiveAuthFailure } from "@/utils/tokenRefreshManager";
import WebSocketManager, {
  CLOSE_CODES,
  getBackoffDelay,
  isRetryableClose,
} from "@/utils/websocket";

import * as userSessionActions from "./user-session.actions";

const webSocketManager = new WebSocketManager();

const NOTIFICATION_WS_ID = "global-notifications";
const CUSTOM_EVENT_WS_ID = "custom-event-reminders";
const MAX_RETRIES = 10;
// On an AUTH_FAILED (4001) streak, refresh the token at most this many times before
// falling back to plain backoff. Prevents a refresh loop when the fresh token is also
// rejected. Reset to 0 on a successful open.
const MAX_AUTH_REFRESHES = 1;

const NOTIFICATION_ACTIONS = {
  GET_ALL_NOTIFICATIONS: "Get all notifications",
  RECEIVING_NOTIFICATION: "Notification",
  READ_NOTIFICATION_MESSAGE: "Reading notifications",
  MARK_ALL_NOTIFICATION_AS_READ: "Mark all as read",
  DELETE_NOTIFICATION: "Delete notification",
};

const CUSTOM_EVENT_ACTIONS = {
  GET_ALL_MESSAGES: "Get all messages",
  GET_MESSAGE: "Get message",
};

function getFreshAccessToken() {
  try {
    const raw = window.localStorage.getItem("auth");
    if (!raw) {
      return null;
    }
    const auth = JSON.parse(raw);
    return auth?.access_token ?? null;
  } catch {
    return null;
  }
}

export function createNotificationSocket(dispatch, userId) {
  let retryCount = 0;
  let authRefreshCount = 0;
  let reconnectTimer = null;
  let shouldReconnect = true;

  const scheduleReconnect = (code) => {
    if (!shouldReconnect) {
      return;
    }
    if (retryCount >= MAX_RETRIES) {
      console.error(`Notification WebSocket: Max retries (${MAX_RETRIES}) reached.`);
      return;
    }
    retryCount += 1;
    const delay =
      code === CLOSE_CODES.RATE_LIMITED
        ? getBackoffDelay(retryCount, { baseDelay: 10000, maxDelay: 60000 })
        : getBackoffDelay(retryCount);
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
    }
    reconnectTimer = setTimeout(() => connect(), delay);
  };

  // On AUTH_FAILED, the stored token is expired. Proactively refresh it before
  // reconnecting so an idle tab (no HTTP traffic to trip the axios 401 refresh)
  // self-heals instead of retrying the stale token until MAX_RETRIES.
  const handleAuthFailure = async () => {
    if (!shouldReconnect) {
      return;
    }
    if (authRefreshCount >= MAX_AUTH_REFRESHES) {
      // Already refreshed this streak and still rejected: fall back to capped backoff.
      scheduleReconnect(CLOSE_CODES.AUTH_FAILED);
      return;
    }
    authRefreshCount += 1;
    try {
      await dispatch(authenticationActions.refreshToken());
    } catch (error) {
      if (isDefinitiveAuthFailure(error)) {
        // Definitively rejected (400/401): refreshToken has already dispatched
        // logout + reset itself, so dispatching logout() again here only POSTs
        // revoke_token/ a second time. Stop reconnecting on a dead session.
        shouldReconnect = false;
        if (reconnectTimer) {
          clearTimeout(reconnectTimer);
        }
        return;
      }
      // Transient failure (network error / 5xx): the refresh never got a verdict,
      // so the session is still good. Hold it and retry the socket rather than
      // ending the session — this bare catch used to log the user out on a single
      // blip, which is what QA saw mid-session.
      //
      // Hand the attempt back before retrying. It produced no verdict, so it
      // must not count against MAX_AUTH_REFRESHES (which is 1): otherwise the
      // next AUTH_FAILED skips the refresh entirely and reconnects with the same
      // stale token until MAX_RETRIES runs out. That would strand exactly the
      // idle tab this handler exists to rescue, since with no HTTP traffic
      // nothing else will refresh the token for it. Still bounded — reconnects
      // are capped by MAX_RETRIES with backoff, and both counters reset on a
      // successful open.
      authRefreshCount -= 1;
      scheduleReconnect(CLOSE_CODES.AUTH_FAILED);
      return;
    }
    if (!getFreshAccessToken()) {
      // Refresh failed: refreshToken already dispatched logout + reset (the normal
      // session-expiry path). Stop reconnecting rather than retry a dead session.
      shouldReconnect = false;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      return;
    }
    // Token refreshed: the next connect() reads the fresh token via getFreshAccessToken().
    scheduleReconnect(CLOSE_CODES.AUTH_FAILED);
  };

  const connect = async () => {
    if (!shouldReconnect) {
      return;
    }

    const accessToken = getFreshAccessToken();
    if (!accessToken) {
      return;
    }

    try {
      await webSocketManager.createWebSocket(
        NOTIFICATION_WS_ID,
        `${SOCKET_URL}user_connect/${userId}`,
        {
          onMessage: (event) => {
            let data;
            try {
              data = camelcaseKeys(JSON.parse(event.data), { deep: true });
            } catch (error) {
              console.error("Notification socket: failed to parse message", error);
              return;
            }

            switch (data.type) {
              case NOTIFICATION_ACTIONS.GET_ALL_NOTIFICATIONS:
                dispatch(notificationsActions.setAllChatNotifications(data.messages));
                break;
              case NOTIFICATION_ACTIONS.RECEIVING_NOTIFICATION:
                dispatch(notificationsActions.receiveChatNotification(data.message));
                dispatch(
                  notificationsActions.setNotification(
                    data.message?.purchaseOrderId ? "purchase_order" : "order",
                    data.message
                  )
                );
                break;
              case NOTIFICATION_ACTIONS.READ_NOTIFICATION_MESSAGE: {
                const ids = camelcaseKeys(data.messages, { deep: true })?.notificationId;
                if (ids?.[0]) {
                  dispatch(notificationsActions.markChatNotificationRead(ids[0]));
                }
                break;
              }
              case NOTIFICATION_ACTIONS.MARK_ALL_NOTIFICATION_AS_READ:
                dispatch(notificationsActions.markAllChatNotificationsRead());
                break;
              case NOTIFICATION_ACTIONS.DELETE_NOTIFICATION: {
                const notificationId = camelcaseKeys(data.message, { deep: true })?.notificationId;
                if (notificationId) {
                  dispatch(notificationsActions.deleteChatNotification(notificationId));
                }
                break;
              }
              default:
                break;
            }
          },
          onClose: (event) => {
            if (!shouldReconnect) {
              return;
            }
            if (event.code === CLOSE_CODES.AUTH_FAILED) {
              handleAuthFailure();
              return;
            }
            if (isRetryableClose(event.code)) {
              scheduleReconnect(event.code);
            }
          },
          onError: () => {
            webSocketManager.closeWebSocket(NOTIFICATION_WS_ID);
          },
        }
      );

      retryCount = 0;
      authRefreshCount = 0;
    } catch {
      // onClose is the single source of reconnect scheduling; do not double-schedule here.
    }
  };

  connect();

  return () => {
    shouldReconnect = false;
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
    }
    webSocketManager.closeWebSocket(NOTIFICATION_WS_ID);
  };
}

export function createCustomEventSocket(dispatch, userId) {
  let retryCount = 0;
  let authRefreshCount = 0;
  let reconnectTimer = null;
  let shouldReconnect = true;

  const scheduleReconnect = (code) => {
    if (!shouldReconnect) {
      return;
    }
    if (retryCount >= MAX_RETRIES) {
      console.error(`Custom event WebSocket: Max retries (${MAX_RETRIES}) reached.`);
      return;
    }
    retryCount += 1;
    const delay =
      code === CLOSE_CODES.RATE_LIMITED
        ? getBackoffDelay(retryCount, { baseDelay: 10000, maxDelay: 60000 })
        : getBackoffDelay(retryCount);
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
    }
    reconnectTimer = setTimeout(() => connect(), delay);
  };

  // On AUTH_FAILED, the stored token is expired. Proactively refresh it before
  // reconnecting so an idle tab (no HTTP traffic to trip the axios 401 refresh)
  // self-heals instead of retrying the stale token until MAX_RETRIES.
  const handleAuthFailure = async () => {
    if (!shouldReconnect) {
      return;
    }
    if (authRefreshCount >= MAX_AUTH_REFRESHES) {
      // Already refreshed this streak and still rejected: fall back to capped backoff.
      scheduleReconnect(CLOSE_CODES.AUTH_FAILED);
      return;
    }
    authRefreshCount += 1;
    try {
      await dispatch(authenticationActions.refreshToken());
    } catch (error) {
      if (isDefinitiveAuthFailure(error)) {
        // Definitively rejected (400/401): refreshToken has already dispatched
        // logout + reset itself, so dispatching logout() again here only POSTs
        // revoke_token/ a second time. Stop reconnecting on a dead session.
        shouldReconnect = false;
        if (reconnectTimer) {
          clearTimeout(reconnectTimer);
        }
        return;
      }
      // Transient failure (network error / 5xx): the refresh never got a verdict,
      // so the session is still good. Hold it and retry the socket rather than
      // ending the session — this bare catch used to log the user out on a single
      // blip, which is what QA saw mid-session.
      //
      // Hand the attempt back before retrying. It produced no verdict, so it
      // must not count against MAX_AUTH_REFRESHES (which is 1): otherwise the
      // next AUTH_FAILED skips the refresh entirely and reconnects with the same
      // stale token until MAX_RETRIES runs out. That would strand exactly the
      // idle tab this handler exists to rescue, since with no HTTP traffic
      // nothing else will refresh the token for it. Still bounded — reconnects
      // are capped by MAX_RETRIES with backoff, and both counters reset on a
      // successful open.
      authRefreshCount -= 1;
      scheduleReconnect(CLOSE_CODES.AUTH_FAILED);
      return;
    }
    if (!getFreshAccessToken()) {
      // Refresh failed: refreshToken already dispatched logout + reset (the normal
      // session-expiry path). Stop reconnecting rather than retry a dead session.
      shouldReconnect = false;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      return;
    }
    // Token refreshed: the next connect() reads the fresh token via getFreshAccessToken().
    scheduleReconnect(CLOSE_CODES.AUTH_FAILED);
  };

  const connect = async () => {
    if (!shouldReconnect) {
      return;
    }

    const accessToken = getFreshAccessToken();
    if (!accessToken) {
      return;
    }

    try {
      const ws = await webSocketManager.createWebSocket(
        CUSTOM_EVENT_WS_ID,
        `${SOCKET_URL}custom_event_reminders/${userId}`,
        {
          onMessage: (event) => {
            const state = store.getState();
            let data;
            try {
              data = JSON.parse(event.data);
            } catch (error) {
              console.error("Custom event socket: failed to parse message", error);
              return;
            }
            const messages = camelcaseKeys(data.messages, { deep: true });
            const customEventReminders = plannerSelectors.getCustomEventReminders(state);

            switch (data.type) {
              case CUSTOM_EVENT_ACTIONS.GET_ALL_MESSAGES:
                dispatch(plannerActions.setCustomEventReminders(messages));
                break;
              case CUSTOM_EVENT_ACTIONS.GET_MESSAGE: {
                dispatch(notificationsActions.setNotification("custom_event", messages));
                dispatch(plannerActions.setCustomEventReminder(messages));
                const allReminders = customEventReminders.concat(messages);
                allReminders.sort((a, b) => a.createdTimestampUnix - b.createdTimestampUnix);
                dispatch(plannerActions.setCustomEventReminders(allReminders));
                break;
              }
              default:
                break;
            }
          },
          onClose: (event) => {
            if (!shouldReconnect) {
              return;
            }
            if (event.code === CLOSE_CODES.AUTH_FAILED) {
              handleAuthFailure();
              return;
            }
            if (isRetryableClose(event.code)) {
              scheduleReconnect(event.code);
            }
          },
          onError: () => {
            webSocketManager.closeWebSocket(CUSTOM_EVENT_WS_ID);
          },
        }
      );

      retryCount = 0;
      authRefreshCount = 0;

      ws.send(JSON.stringify({ type: CUSTOM_EVENT_ACTIONS.GET_ALL_MESSAGES }));
    } catch {
      // onClose is the single source of reconnect scheduling; do not double-schedule here.
    }
  };

  connect();

  return () => {
    shouldReconnect = false;
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
    }
    webSocketManager.closeWebSocket(CUSTOM_EVENT_WS_ID);
  };
}

const UserSession = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { replace } = useHistory();
  const query = queryString.parse(location.search);
  const cleanupRef = useRef([]);
  const isMountedRef = useRef(true);

  const getCurrentUser = async () => {
    dispatch(customers.actions.getCurrentUser()).then((userResponse) => {
      dispatch(userSessionActions.setCurrentUser(camelcaseKeys(userResponse, { deep: true })));
      dispatch(customers.actions.getCompanyData()).then(async (response) => {
        dispatch(companySessionActions.setCurrentCompany(camelcaseKeys(response, { deep: true })));
        dispatch(
          customers.actions.submitIntercomUserSettings({ user: userResponse, company: response })
        );
        identifyClarityUser({ user: userResponse, company: response });

        const {
          settings,
          accounting_sync_allowed: accountingSyncAllowed,
          accounting_name: accountingName,
          accounting_layout: accountingLayout,
        } = response;

        if (accountingSyncAllowed) {
          if (accountingName === "MYOB") {
            dispatch(accountingActions.fetchMyobJobs());

            if (accountingLayout === "service") {
              dispatch(accountingActions.fetchMyobAccounts());
            }
          } else if (accountingName === "Xero") {
            const xeroConnectionStatus = await dispatch(
              accountingActions.fetchXeroConnectionStatus()
            );

            const allowFactoryMapToTrackingCategories =
              settings?.accounting_settings?.accounting?.find(
                (a) => a.key === "ACCOUNTING_MAP_ITEMS_TO_TRACKING_CATEGORIES"
              )?.is_selected ?? false;

            if (allowFactoryMapToTrackingCategories) {
              dispatch(accountingActions.fetchXeroTrackingCategories());
            }

            if (!xeroConnectionStatus.isConnected) {
              dispatch(customers.actions.clearAccountingIntegration());
              dispatch(customers.actions.disconnectXero());
            }
          }
        } else if (
          !accountingSyncAllowed &&
          userResponse.auth_provider === "xero" &&
          response.accounting_company_id !== null
        ) {
          const xeroConnectionStatus = await dispatch(
            accountingActions.fetchXeroConnectionStatus()
          );

          if (!xeroConnectionStatus.isConnected) {
            dispatch(customers.actions.clearAccountingIntegration());
            dispatch(customers.actions.disconnectXero());
          }
        }

        if (!response.is_legacy && response?.subscription_status?.name === "canceled") {
          if (accountingSyncAllowed && accountingName === "Xero") {
            dispatch(customers.actions.clearAccountingIntegration());
            dispatch(customers.actions.disconnectXero());
          } else if (
            (!accountingSyncAllowed || accountingName !== "Xero") &&
            userResponse.auth_provider === "xero" &&
            response.accounting_company_id !== null
          ) {
            dispatch(customers.actions.clearAccountingIntegration());
            dispatch(customers.actions.disconnectXero());
          }
        }

        dispatch(authenticationActions.setIsCheckingAuth(false));
      });

      // Start global WebSocket connections after user is loaded. Guard against
      // the component unmounting before this async chain resolves: without it the
      // cleanup return below would have already run, so these sockets would be
      // created after cleanup and never torn down.
      if (!isMountedRef.current) {
        return;
      }
      const userId = userResponse.id;
      cleanupRef.current.push(createNotificationSocket(dispatch, userId));
      cleanupRef.current.push(createCustomEventSocket(dispatch, userId));
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only auth bootstrap; re-running on URL or callback identity changes would re-init websockets and refetch user data.
  useEffect(() => {
    // Reset on every mount cycle: the useRef initializer only runs once, so in
    // StrictMode (mount → cleanup → remount) the cleanup below would leave this
    // false and the socket-setup guard would skip valid initialization.
    isMountedRef.current = true;

    (async () => {
      dispatch(authenticationActions.setIsCheckingAuth(true));

      // This for payment from stripe
      // when stripe goes back to the factory page
      if (query.session_id) {
        await dispatch(
          billingActions.checkoutSuccess({
            session_id: query.session_id,
          })
        );

        replace("/settings?tab=billing");
      }

      await dispatch(authenticationActions.checkAuth());

      if (!query.token) {
        dispatch(timezonesActions.fetchTimezones());
        await getCurrentUser();
      } else {
        dispatch(authenticationActions.setIsCheckingAuth(false));
      }
    })();

    return () => {
      isMountedRef.current = false;
      cleanupRef.current.forEach((cleanup) => {
        cleanup();
      });
      cleanupRef.current = [];
    };
  }, []);

  return <BillingSuccessfulPaymentModal />;
};

export default UserSession;
